/**
 * DataMgmtHome — SuperAdmin Data Management surface.
 *
 * Lists existing data-management actions surfaced across SysOps and surfaces
 * placeholders for future operations (settings editor, schema migrations,
 * bulk imports). Cross-linked to existing pages where the action already
 * exists rather than duplicating UI.
 */

import { Link } from 'react-router';
import {
  Database,
  Download,
  History,
  Mail,
  Settings2,
  Skull,
  TestTube2,
  Upload,
} from 'lucide-react';

export function DataMgmtHome(): JSX.Element {
  return (
    <>
      <section className="page-header">
        <h1 className="page-header__title">Data Management</h1>
        <p className="page-header__subtitle">
          SuperAdmin-tier surface for catalog data, audit history, and
          destructive cleanup. Active actions cross-link to their primary
          page; placeholders denote operations pending build.
        </p>
      </section>

      <section className="tile-grid" aria-label="Data Management actions">
        <Tile
          to="/kill-list"
          icon={<Skull size={20} />}
          title="Kill List"
          body="Verify and execute destructive cleanup requests for CIFs, accounts, and cards. Audit-logged with verifier + executor."
          status="active"
        />
        <Tile
          to="/audit/purge-history"
          icon={<History size={20} />}
          title="Purge History"
          body="Read-only mirror of the SuperAdmin Records Mgmt audit log."
          status="active"
        />
        <Tile
          to="/audit/notifications-history"
          icon={<Mail size={20} />}
          title="Notifications History"
          body="Full searchable NotificationDispatches history with CSV export."
          status="active"
        />
        <Tile
          to="/audit/test-mode-activity"
          icon={<TestTube2 size={20} />}
          title="Test Mode Activity"
          body="Test-Mode + Training-Mode session log. Filter by date, actor, or session."
          status="active"
        />
        <Tile
          to=""
          icon={<Settings2 size={20} />}
          title="System Settings Editor"
          body="Edit dbo.SystemSettings rows (testModeEnabled, admin.auth.allowBypassKey, retention windows, etc.)."
          status="placeholder"
        />
        <Tile
          to=""
          icon={<Database size={20} />}
          title="Schema Migrations"
          body="Apply EF migrations + run seed SQL via the admin endpoints. Currently driven via PowerShell scripts."
          status="placeholder"
        />
        <Tile
          to=""
          icon={<Upload size={20} />}
          title="Bulk User Import"
          body="Upload a CSV of users + role assignments. Today this is handled by walkthrough-prep PowerShell + Entra sync."
          status="placeholder"
        />
        <Tile
          to=""
          icon={<Download size={20} />}
          title="Data Export"
          body="Generate point-in-time exports of catalog tables for compliance and audit review."
          status="placeholder"
        />
      </section>
    </>
  );
}

function Tile({
  to,
  icon,
  title,
  body,
  status,
}: {
  to: string;
  icon: JSX.Element;
  title: string;
  body: string;
  status: 'active' | 'placeholder';
}): JSX.Element {
  const inner = (
    <>
      <div className="tile__icon" aria-hidden="true">
        {icon}
      </div>
      <div className="tile__title">
        {title}
        {status === 'placeholder' && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--ink-600, #6b7280)',
              border: '1px solid var(--ink-300, #d1d5db)',
              padding: '1px 6px',
              borderRadius: 4,
            }}
          >
            coming soon
          </span>
        )}
      </div>
      <div className="tile__body">{body}</div>
    </>
  );
  if (status === 'placeholder') {
    return (
      <div
        className="tile"
        style={{ opacity: 0.6, cursor: 'not-allowed' }}
        aria-disabled="true"
      >
        {inner}
      </div>
    );
  }
  return (
    <Link className="tile" to={to}>
      {inner}
    </Link>
  );
}
